import type { AuditTimelineEntry } from '../../types/auditTimeline';

// Pure: an audit timeline entry → a friendly Plan-activity line (icon +
// one-sentence summary). Slice B §6.2 / §8.2 — internal action codes
// translate to plain English. Plan-scoped, so the vocabulary differs from
// the rollout row timeline (that's why this lives in the plans module, not
// shared with describeAuditEntry).
//
// Kept pure (no React) for a focused unit test.

export type PlanActivityDescription = {
  icon: string;
  summary: string;
};

function shortActor(email: string): string {
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

// Best-effort task/thread title from the diff: a task create records the
// title as a change (after); a delete records it (before). A status/notes-
// only update carries no title change → we degrade to "a task" (the audit
// doc only holds changed fields; same pragmatic degradation the row
// timeline uses). Quotes + truncates so one long title can't overrun.
function titleFromChanges(entry: AuditTimelineEntry): string | null {
  const c = entry.changes.find((x) => x.field === 'title');
  if (!c) return null;
  const v = (c.after ?? c.before) as unknown;
  if (typeof v !== 'string' || v.length === 0) return null;
  return v.length > 60 ? `${v.slice(0, 57)}…` : v;
}

function quoted(t: string | null): string {
  return t ? `“${t}”` : 'a task';
}

const TASK_STATUS_LABEL: Record<string, string> = {
  not_started: 'Not Started',
  on_hold: 'On Hold',
  in_progress: 'In Progress',
  in_review: 'In Review',
  completed: 'Completed',
};

const PLAN_FIELD_LABEL: Record<string, string> = {
  name: 'name',
  overview: 'overview',
  offerMechanicsUrl: 'offer mechanics URL',
  kyckCampaignOverviewUrl: 'Kyck campaign overview URL',
  promoStartDate: 'promo start date',
  promoEndDate: 'promo end date',
  rolloutSlug: 'rollout slug',
  monthYearLabel: 'month-year label',
  notes: 'notes',
  defaultCampaignId: 'default Campaign',
  status: 'status',
  campaignIds: 'attached Campaigns',
  rolloutId: 'linked Rollout',
  datesLocked: 'date lock',
};

// Audit bookkeeping fields never read as a meaningful change.
const NOISE_FIELDS = new Set([
  'updatedAt',
  'updatedBy',
  'createdAt',
  'createdBy',
]);

function describeTask(
  entry: AuditTimelineEntry,
  actor: string,
): PlanActivityDescription {
  const title = quoted(titleFromChanges(entry));
  if (entry.operation === 'create') {
    return { icon: '➕', summary: `${actor} added task ${title}` };
  }
  if (entry.operation === 'delete') {
    return { icon: '🗑', summary: `${actor} deleted task ${title}` };
  }
  // update — inspect what changed (priority order: completion, status,
  // assignment, notes, then a generic edit).
  const statusChange = entry.changes.find((c) => c.field === 'status');
  if (statusChange) {
    const next = String(statusChange.after ?? '');
    if (next === 'completed') {
      return {
        icon: '✅',
        summary: `${actor} marked ${title} as Completed`,
      };
    }
    return {
      icon: '🔄',
      summary: `${actor} moved ${title} to ${
        TASK_STATUS_LABEL[next] ?? next
      }`,
    };
  }
  if (
    entry.changes.some(
      (c) => c.field === 'assignedUserId' || c.field === 'assignedTeam',
    )
  ) {
    return { icon: '👤', summary: `${actor} reassigned ${title}` };
  }
  if (entry.changes.some((c) => c.field === 'notes')) {
    return { icon: '📝', summary: `${actor} updated notes on ${title}` };
  }
  return { icon: '✏️', summary: `${actor} edited ${title}` };
}

function describeThread(
  entry: AuditTimelineEntry,
  actor: string,
): PlanActivityDescription {
  if (entry.operation === 'create') {
    const typeChange = entry.changes.find((c) => c.field === 'type');
    if (String(typeChange?.after ?? '') === 'task_note') {
      const re = entry.changes.find(
        (c) => c.field === 'taskTitleSnapshot',
      );
      const t =
        typeof re?.after === 'string' && re.after.length > 0
          ? ` (re: ${re.after})`
          : '';
      return {
        icon: '📝',
        summary: `${actor} logged a task note${t}`,
      };
    }
    return { icon: '💬', summary: `${actor} posted a comment` };
  }
  // A soft delete sets deletedAt; otherwise a comment edit.
  if (entry.changes.some((c) => c.field === 'deletedAt' && c.after)) {
    return { icon: '🗑', summary: `${actor} deleted a comment` };
  }
  return { icon: '✏️', summary: `${actor} edited a comment` };
}

function describePlanDoc(
  entry: AuditTimelineEntry,
  actor: string,
): PlanActivityDescription {
  const ctx = entry.context ?? '';

  if (entry.operation === 'create') {
    return { icon: '📋', summary: `${actor} created this Plan` };
  }

  // Context-scoped events (the writer stamps a human context).
  if (ctx.startsWith('applied template')) {
    return { icon: '📋', summary: `${actor} ${ctx}` };
  }
  if (ctx.startsWith('status ')) {
    const m = /^status\s+(\S+)\s*->\s*(\S+)$/.exec(ctx);
    return {
      icon: '🔄',
      summary: m
        ? `${actor} changed status from ${m[1]} to ${m[2]}`
        : `${actor} changed status`,
    };
  }
  if (ctx.startsWith('campaign attach')) {
    return { icon: '➕', summary: `${actor} attached a Campaign` };
  }
  if (ctx.startsWith('campaign detach')) {
    return { icon: '➖', summary: `${actor} detached a Campaign` };
  }
  if (ctx.startsWith('campaign reorder')) {
    return { icon: '↕️', summary: `${actor} reordered Campaigns` };
  }
  if (ctx.startsWith('connected rollout')) {
    return { icon: '🚀', summary: `${actor} created a Rollout` };
  }
  if (ctx.startsWith('Plan unlocked')) {
    return {
      icon: '🔗',
      summary: `${actor} unlinked the Rollout`,
    };
  }

  // Generic metadata edit — friendly per-field summary, noise suppressed.
  const meaningful = entry.changes.filter(
    (c) => !NOISE_FIELDS.has(c.field),
  );
  if (meaningful.length === 0) {
    return { icon: '✏️', summary: `${actor} updated the Plan` };
  }
  if (meaningful.length === 1) {
    const f = meaningful[0]!.field;
    return {
      icon: '✏️',
      summary: `${actor} updated the ${PLAN_FIELD_LABEL[f] ?? f}`,
    };
  }
  return {
    icon: '✏️',
    summary: `${actor} updated the Plan (${meaningful.length} fields)`,
  };
}

export function describePlanActivity(
  entry: AuditTimelineEntry,
): PlanActivityDescription {
  const actor = shortActor(entry.actor);
  switch (entry.entityType) {
    case 'promotionPlanTask':
      return describeTask(entry, actor);
    case 'promotionPlanThread':
      return describeThread(entry, actor);
    case 'promotionPlan':
      return describePlanDoc(entry, actor);
    default:
      return { icon: '•', summary: `${actor} made a change` };
  }
}
